<template>
  <div>
    <div class="absolute-elements">
      <NavBar :sections="sections" @scroll-to-section="scrollToSection" />
    </div>
    <div class="h-screen w-screen overflow-y-auto bg-base-100" @scroll="handleScroll">
    <WaveAnimation :visible="showWave" />
      <section class="section min-h-screen" id="slide1">
        <div id="slide1-container">
          <!-- Update paths to remove @ -->
          <BackgroundVideo
            desktopVideo="/videos/HomeLandingVideo.mp4"
            mobileVideo="/videos/HomeLandingVideoMobile.mp4"
            placeholderImage="/videos/HomeLandingVideo.jpg"
            altText="STE Manufacturing Facility"
            videoId="homeVideo"
            @video-loaded="handleVideoLoad"
            @video-error="handleVideoError"
          />
          <div class="slideText">
            <div class="sub-text-with-image">
              <div class="text-content">
                <h3 class="slideSub text-5xl rounded-4xl bg-primary/70 border-1 border-base-content text-base-content p-4">An Unmistakable Commitment to Precision...</h3>
                <h4 class="ml-4 mt-6 text-xl text-secondary">Build to Spec Stainless Steel Tank Trailers</h4>
                <div class="ml-4 mt-6 border-b-2 border-base-content w-1/3"></div>
              </div>
            </div>
          </div>
        </div>
        <div v-if="videoLoading && !imageLoaded" class="loading-spinner">
          <font-awesome-icon :icon="['fas', 'spinner']" spin />
        </div>
      </section>
      <section class="section min-h-screen h-full" id="slide2">
        <div class="section-content min-h-screen h-full">
          <TakeATour />
        </div>
      </section>
      <section class="section min-h-screen" id="slide3">
        <div class="section-content">
          <InfoBoxGrid />
        </div>
      </section>
      <section class="section min-h-screen" id="slide4">
        <div class="section-content min-h-screen">
          <ValueSlider />
        </div>
      </section>
      <section class="section min-h-[100vh]" id="slide5">
        <div class="section-content">
          <PartsAndService />
        </div>
      </section>
      <section class="section" id="slide6">
        <div class="section-content">
          <JoinUsBox />
        </div>
      </section>
      <section class="section" id="slide7">
        <div class="section-content">
          <ContactSection />
        </div>
      </section>
    </div>
    <transition name="fade">
      <font-awesome-icon
        v-if="showScrollUp"
        :icon="isUpHovered ? 'angles-up' : 'chevron-up'"
        class="scroll-arrow up-arrow fixed bottom-[60px] left-[51.5%] transform -translate-x-1/2 text-4xl text-secondary cursor-pointer z-50 hover:scale-125 transition-transform"
        @click="scrollToPreviousSection"
        @mouseenter="isUpHovered = true"
        @mouseleave="isUpHovered = false"
      />
    </transition>
    <transition name="fade">
      <font-awesome-icon
        v-if="showScrollDown"
        :icon="isDownHovered ? 'angles-down' : 'chevron-down'"
        class="scroll-arrow down-arrow fixed bottom-[10px] left-[51.5%] transform -translate-x-1/2 text-4xl text-secondary cursor-pointer z-50 hover:scale-125 transition-transform"
        @click="scrollToNextSection"
        @mouseenter="isDownHovered = true"
        @mouseleave="isDownHovered = false"
      />
    </transition>
  </div>
</template>

<script>
import JoinUsBox from '@/components/JoinUsBox.vue';
import NavBar from '@/components/NavBar.vue';
import InfoBoxGrid from '@/components/InfoBoxGrid.vue';
import ValueSlider from '@/components/ValueSlider.vue';
import ContactSection from '@/components/ContactSection.vue';
import TakeATour from '@/components/TakeATour.vue';
import PartsAndService from '@/components/PartsAndService.vue';
import BackgroundVideo from '@/components/BackgroundVideo.vue';
import WaveAnimation from '@/components/WaveAnimation.vue';
import { nextTick } from 'vue';

export default {
  name: 'HomePage',
  components: {
    JoinUsBox,
    NavBar,
    InfoBoxGrid,
    ValueSlider,
    ContactSection,
    TakeATour,
    PartsAndService,
    BackgroundVideo,
    WaveAnimation,
  },
  data() {
    return {
      showScrollUp: false,
      showScrollDown: true,
      isUpHovered: false,
      isDownHovered: false,
      showWave: false,
      videoLoading: true,
      videoReady: false,
      imageLoaded: true, // Set to true by default to avoid showing spinner when image loads quickly
      sections: [
        { id: 'slide2', name: 'NEWS', bgClass: 'bg-gray-200' },
        { id: 'slide3', name: 'TRAILERS', bgClass: 'bg-gray-200' },
        { id: 'slide4', name: 'ABOUT', bgClass: 'bg-gray-300' },
        { id: 'slide5', name: 'PARTS & SERVICE', bgClass: 'bg-gray-400' },
        { id: 'slide6', name: 'CAREERS', bgClass: 'bg-gray-400' },
        { id: 'slide7', name: 'CONTACT', bgClass: 'bg-gray-400' },
      ],
    };
  },
  mounted() {
    this.initIntersectionObserver();
    this.handleScroll();

    this.scrollToHash();

    // Watch for route changes to handle hash scrolling
    this.$watch('$route', (to) => {
      if (to.hash) {
        this.scrollToHash();
      }
    });
  },
  beforeUnmount() {
    if (this.observer) {
      this.observer.disconnect();
    }
  },
  methods: {
    initIntersectionObserver() {
      const options = {
        root: null,
        rootMargin: '0px',
        threshold: 0.1,
      };

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            console.log(`Section ${entry.target.id} is now visible`);
          } else {
            entry.target.classList.remove('visible');
            console.log(`Section ${entry.target.id} is now hidden`);
          }
        });
      }, options);

      document.querySelectorAll('section').forEach((section) => {
        this.observer.observe(section);
      });
    },
    handleScroll() {
      const container = document.querySelector('.overflow-y-auto');
      this.showScrollUp = container.scrollTop > 100;
      this.showScrollDown = container.scrollTop + container.clientHeight < container.scrollHeight - 100;
      this.showWave = container.scrollTop > container.clientHeight / 2;
    },
    scrollToNextSection() {
      const container = document.querySelector('.overflow-y-auto');
      const sections = Array.from(document.querySelectorAll('section'));
      const currentScroll = container.scrollTop;
      const tolerance = 5;

      const nextSection = sections.find(
        (section) => section.offsetTop > currentScroll + tolerance
      );

      if (nextSection) {
        console.log(`Scrolling to next section: ${nextSection.id}`);
        container.scrollTo({ top: nextSection.offsetTop, behavior: 'smooth' });
      } else {
        console.log('No next section found');
      }
    },
    scrollToPreviousSection() {
      const container = document.querySelector('.overflow-y-auto');
      const sections = Array.from(document.querySelectorAll('section'));
      const currentScroll = container.scrollTop;
      const tolerance = 5;

      let previousSection = null;
      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        if (section.offsetTop + tolerance < currentScroll) {
          previousSection = section;
          break;
        }
      }

      if (previousSection) {
        console.log(`Scrolling to previous section: ${previousSection.id}`);
        container.scrollTo({ top: previousSection.offsetTop, behavior: 'smooth' });
      } else {
        this.scrollToTop();
      }
    },
    scrollToTop() {
      const container = document.querySelector('.overflow-y-auto');
      container.scrollTo({ top: 0, behavior: 'smooth' });
    },
    scrollToSection(id) {
      const container = document.querySelector('.overflow-y-auto');
      const element = document.getElementById(id);
      if (element) {
        container.scrollTo({ top: element.offsetTop, behavior: 'smooth' });
      }
    },
    scrollToHash() {
      nextTick(() => {
        setTimeout(() => {
          const hash = this.$route.hash;
          if (hash) {
            const element = document.querySelector(hash);
            if (element) {
              const container = document.querySelector('.overflow-y-auto');
              container.scrollTo({ top: element.offsetTop, behavior: 'smooth' });
            }
          }
        }, 500);
      });
    },
    handleVideoLoad() {
      this.videoLoading = false;
      this.videoReady = true;
      
      // Ensure the video starts playing
      const video = document.getElementById('bgVideo');
      if (video) {
        video.play().catch(err => console.error('Error playing video:', err));
      }
    },
    handleVideoError() {
      console.error('Error loading video');
      this.videoLoading = false;
    },
  },
};
</script>

<style scoped lang="scss">
.absolute-elements {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  display: flex;
  z-index: 100;
}

// Replace fullpage-container styles
.h-screen.overflow-y-auto {
  background: radial-gradient(ellipse at bottom, var(--color-base-100) 0%, var(--color-base-300) 90%);
  background-attachment: fixed;
  position: relative;
  scrollbar-width: none;;
  z-index: 0;
  -webkit-overflow-scrolling: touch; /* For smoother scrolling on iOS devices */
}

#slide1-container {
  z-index: -25;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: left;
  min-height:100vh;
  width: 100%;
  position: relative;
  overflow: hidden; /* Add this to contain absolutely positioned children */
}

#bgVideo {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0; // Start with lower z-index
  opacity: 0;
  transition: opacity 0.5s ease;
}

#bgVideo.video-loaded {
  z-index: 2; // Higher than placeholder but lower than .slideText
  opacity: 1;
}

.loading-spinner {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 2rem;
  color: white;
}

.section {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  z-index: 1;
  width: 100%;
  overflow: hidden;
}

.section-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.slideText {
  max-width: 70%;
  margin-top: 70vh;
  margin-left: 2vw;
  text-align: left;
  display: flex;
  flex-direction: column;
  justify-content: center;
  color: rgb(183, 185, 186);
  position: relative;
  z-index: 10; /* Add higher z-index to ensure it stays above video */

  @media (max-width: 768px) {
    margin-top: 58vh;
    margin-left: 1vw;
    max-width: 100%;
    text-align: left;
  }
}

.sub-text-with-image {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.text-content {
  display: flex;
  flex-direction: column;
}

.slideTitle {
  color: rgb(183, 185, 186);
  line-height: 1.25;
}

.slideSuperSub {
  line-height: 1;
  outline: 2px inset rgb(255, 255, 255);
  padding: 0.5rem;
  font-weight: bold;
  max-width: 40%;
  text-align: center;
  border-radius: 4rem;
  background: #8f0000;
  color: #fff;

  @media (max-width: 768px) {
    max-width: 55%;
  }
}

// Update scroll arrow styles to match LaChanceExample
.scroll-arrow {
  animation: bob 2s infinite;
  transition: transform 0.3s, opacity 0.3s;
  z-index: 100;
}

.up-arrow {
  @media (max-width: 768px) {
    visibility: hidden;
  }
}

@keyframes bob {
  0%, 100% {
    transform: translate(-50%, 0);
  }
  50% {
    transform: translate(-50%, 10px);
  }
}

.noshow {
  @media (max-width: 768px) {
    display: none;
  }
}

.mobile {
  @media (min-width: 768px) {
    display: none;
  }
}

body.modal-active .scroll-arrow {
  display: none !important;
}

body.modal-open {
  overflow: hidden;
  touch-action: none;
  position: fixed;
  width: 100%;
  height: 100%;
}

/* Add fade animation for transitions */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

// Add styles for video and placeholder
.video-placeholder {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 1;
}
</style>
